import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import TopSection from "../../LT/TopSection";
import ButtonComponent from "../../../components/common/button/Button";
import { MultiDropdown } from "../../../components/common/MultiDropdown";
import QuotaArrangementGrid from "./QuotaArrangementGrid";
import { Loader } from "../../../components/common/Loader";
import {
  fetchQuotaMaintenanceFilterList,
  fetchQuotaMaintenanceSearch,
  fetchQuotaMaintenanceViewAll,
} from "../../../redux/actions/QuotaMaintenance";

const initialQuotaFilters = {
  material: [],
  plant: [],
  flow: [],
  sapNo: [],
  pmVendors: [],
  externalMaterialGroup: [],
  mrpController: [],
  purchasingGroup: [],
  validFrom: [],
  validTo: [],
  sapStatus: [],
  contractValidity: [],
  quotaValidity: [],
};

const QUOTA_FILTERS_STORAGE_KEY = "QuotaFilters";
const QUOTA_SELECTED_CARD_STORAGE_KEY = "QuotaSelectedCard";

const quotaMetricCards = [
  {
    key: "totalArrangements",
    title: "TOTAL QUOTAS",
    status: "",
  },
  { key: "createRequired", title: "CREATE REQUIRED", status: "CREATE DRAFT" },
  { key: "updateRequired", title: "UPDATE REQUIRED", status: "UPDATE REQUIRED" },
  // { key: "activeInSap", title: "ACTIVE IN SAP", status: "ACTIVE IN SAP" },
  { key: "expiringSoon", title: "EXPIRING SOON", status: "EXPIRING SOON" },
  {
    key: "allocationAtMax",
    title: "ALLOCATION AT MAX",
    status: "ALLOCATION AT MAX",
  },
  {
    key: "failedSubmission",
    title: "FAILED SUBMISSION",
    status: "FAILED SUBMISSION",
  },
];

const emptyQuotaFilterOptions = {
  material: [],
  plant: [],
  pmVendors: [],
  externalMaterialGroup: [],
  mrpController: [],
  purchasingGroup: [],
  validFrom: [],
  validTo: [],
};

const cloneFilterItems = (items = []) =>
  (items || []).map((item) =>
    item && typeof item === "object" ? { ...item } : item,
  );

const cloneQuotaFiltersState = (state) => {
  if (!state) return { ...initialQuotaFilters };
  return {
    material: cloneFilterItems(state.material),
    plant: cloneFilterItems(state.plant),
    flow: cloneFilterItems(state.flow),
    sapNo: cloneFilterItems(state.sapNo),
    pmVendors: cloneFilterItems(state.pmVendors),
    externalMaterialGroup: cloneFilterItems(state.externalMaterialGroup),
    mrpController: cloneFilterItems(state.mrpController),
    purchasingGroup: cloneFilterItems(state.purchasingGroup),
    validFrom: cloneFilterItems(state.validFrom),
    validTo: cloneFilterItems(state.validTo),
    sapStatus: cloneFilterItems(state.sapStatus),
    contractValidity: cloneFilterItems(state.contractValidity),
    quotaValidity: cloneFilterItems(state.quotaValidity),
  };
};

const readQuotaFiltersFromStorage = () => {
  try {
    const raw = localStorage.getItem(QUOTA_FILTERS_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;

    return {
      quotaFilters: cloneQuotaFiltersState(parsed.quotaFilters),
      appliedQuotaFilters: cloneQuotaFiltersState(parsed.appliedQuotaFilters),
    };
  } catch {
    return null;
  }
};

const writeQuotaFiltersToStorage = (payload) => {
  try {
    localStorage.setItem(QUOTA_FILTERS_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // ignore storage failures and keep UI usable
  }
};

const clearQuotaFiltersFromStorage = () => {
  try {
    localStorage.removeItem(QUOTA_FILTERS_STORAGE_KEY);
  } catch {
    // no-op
  }
};

const readSelectedQuotaCardFromStorage = () => {
  try {
    const storedKey = localStorage.getItem(QUOTA_SELECTED_CARD_STORAGE_KEY);
    return quotaMetricCards.some((card) => card.key === storedKey)
      ? storedKey
      : "totalArrangements";
  } catch {
    return "totalArrangements";
  }
};

const writeSelectedQuotaCardToStorage = (cardKey) => {
  try {
    localStorage.setItem(QUOTA_SELECTED_CARD_STORAGE_KEY, cardKey);
  } catch {
    // no-op
  }
};

const QuotaMaintenance = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const isResultsPage = location.pathname === "/quota-maintenance/results";
  const quotaMaintenanceDetails = useSelector(
    (state) => state?.QuotaMaintenanceSlice,
  );
  const [apiData, setApiData] = useState([]);
  const hydratedQuotaFilters = useMemo(
    () => readQuotaFiltersFromStorage(),
    [],
  );
  const [quotaFilters, setQuotaFilters] = useState(
    () => hydratedQuotaFilters?.quotaFilters || initialQuotaFilters,
  );
  const [appliedQuotaFilters, setAppliedQuotaFilters] = useState(
    () => hydratedQuotaFilters?.appliedQuotaFilters || initialQuotaFilters,
  );
  const [quotaFilterOptions, setQuotaFilterOptions] = useState(
    emptyQuotaFilterOptions,
  );
  const [selectedQuotaCardKey, setSelectedQuotaCardKey] = useState(
    readSelectedQuotaCardFromStorage,
  );
  const [isFilterOptionsLoaded, setIsFilterOptionsLoaded] = useState(false);
  const procurementDivisionType = localStorage.getItem(
    "procurementDivisionType",
  );

  const toFilterValues = useCallback(
    (items = []) =>
      (items || [])
        .map((item) => item?.value ?? item)
        .filter((value) => value !== undefined && value !== null && value !== ""),
    [],
  );

  const toSingleFilterValue = useCallback(
    (items = []) => toFilterValues(items)[0] || "",
    [toFilterValues],
  );

  const buildFilterListPayload = useCallback(
    (filtersState) => {
      const safeFilters = filtersState || initialQuotaFilters;
      return {
        plantNos: toFilterValues(safeFilters.plant),
        materialNos: toFilterValues(safeFilters.material),
        vendorNos: toFilterValues(safeFilters.pmVendors),
        externalMaterialGroups: toFilterValues(safeFilters.externalMaterialGroup),
        mrpControllers: toFilterValues(safeFilters.mrpController),
        purchasingGroups: toFilterValues(safeFilters.purchasingGroup),
        validFrom: toSingleFilterValue(safeFilters.validFrom),
        validTo: toSingleFilterValue(safeFilters.validTo),
      };
    },
    [toFilterValues, toSingleFilterValue],
  );

  const getQuotaMaintenanceResults = useCallback(async (appliedFilterState) => {
    try {
      const response = await dispatch(
        fetchQuotaMaintenanceSearch({
        
          data: buildFilterListPayload(appliedFilterState),
        }),
      );

      if (response?.meta?.requestStatus !== "fulfilled") {
        toast.error(
          response?.payload?.errorMessage ||
            "Failed to fetch quota maintenance arrangements",
        );
        return false;
      }

      return true;
    } catch (error) {
      toast.error("Error fetching quota maintenance arrangements");
      console.error("Error fetching quota maintenance arrangements:", error);
      return false;
    }
  }, [buildFilterListPayload, dispatch]);

  const cards = useMemo(() => {
    const kpis = quotaMaintenanceDetails?.data?.kpis || {};

    return quotaMetricCards.map((card) => ({
      ...card,
      value: kpis?.[card.key] ?? 0,
    }));
  }, [quotaMaintenanceDetails?.data?.kpis]);

  const selectedQuotaCard = useMemo(
    () =>
      cards.find((card) => card.key === selectedQuotaCardKey) || cards[0],
    [cards, selectedQuotaCardKey],
  );

  const handleQuotaCardClick = (cardKey) => {
    setSelectedQuotaCardKey(cardKey);
    writeSelectedQuotaCardToStorage(cardKey);
  };

  const refreshQuotaMaintenanceResults = useCallback(async () => {
    const hasAppliedFilters = Object.values(appliedQuotaFilters).some(
      (items) => Array.isArray(items) && items.length > 0,
    );

    if (hasAppliedFilters) {
      return getQuotaMaintenanceResults(appliedQuotaFilters);
    }

    try {
      const response = await dispatch(
        fetchQuotaMaintenanceViewAll({
          
        }),
      );

      if (response?.meta?.requestStatus !== "fulfilled") {
        toast.error(
          response?.payload?.errorMessage ||
            "Failed to fetch all quota arrangements",
        );
        return false;
      }

      return true;
    } catch (error) {
      toast.error("Error fetching all quota arrangements");
      console.error("Error fetching all quota arrangements:", error);
      return false;
    }
  }, [
    appliedQuotaFilters,
    dispatch,
    getQuotaMaintenanceResults,
    procurementDivisionType,
  ]);

  useEffect(() => {
    const hasLoadedResults = Object.prototype.hasOwnProperty.call(
      quotaMaintenanceDetails?.data || {},
      "data",
    );

    if (isResultsPage && !hasLoadedResults) {
      refreshQuotaMaintenanceResults();
    }
  }, [
    isResultsPage,
    quotaMaintenanceDetails?.data,
    refreshQuotaMaintenanceResults,
  ]);

  useEffect(() => {
    const responseData = quotaMaintenanceDetails?.data?.data || [];

    const formattedData = responseData.map((item) => ({
      ...item,
      id: `header-${item?.headerId}`,
      material: `${item?.material || ""}${
        item?.materialDescription ? ` - ${item.materialDescription}` : ""
      }`,
      sapNo: item?.sapArrNo,

      children: (item?.children || []).map((child) => ({
        ...child,
        id: `item-${
          child?.itemId ||
          `${item?.headerId}-${child?.vendorNo || child?.vendorName || "vendor"}`
        }`,
        material:
          child?.vendorNo && child?.vendorName
            ? `${child.vendorNo} - ${child.vendorName}`
            : child?.vendorNo || child?.vendorName || "N/A",

        sapNo: child?.vendorStatus || "",
        sapArrNo: child?.vendorStatus || "",

        allocationQty: child?.allocationQty || "",
        totalAllocQty: child?.allocationQty || "",

        pmVendors: child?.quotaPct || "",
        sapStatus: child?.sapMaxqt || "",
        contractValidity: child?.contractValidity || "",
        quotaValidity: child?.quotaValidity || "",
      })),
    }));

    setApiData(formattedData);
  }, [quotaMaintenanceDetails?.data?.data]);

  const toDropdownOptions = useCallback((values = []) => {
    const uniqueValues = [
      ...new Set(values.filter((value) => value !== null && value !== undefined && value !== "")),
    ];

    return uniqueValues
      .sort((a, b) => String(a).localeCompare(String(b)))
      .map((value) => ({
        title: String(value),
        value: String(value),
        name: String(value),
      }));
  }, []);

  const mapFilterApiResponseToOptions = useCallback(
    (filters = {}) => ({
      plant: toDropdownOptions(filters.plant || []),
      material: toDropdownOptions(filters.material || []),
      pmVendors: toDropdownOptions(filters.vendor || []),
      purchasingGroup: toDropdownOptions(filters.purchasingGroup || []),
      externalMaterialGroup: toDropdownOptions(filters.externalMaterialGroup || []),
      mrpController: toDropdownOptions(filters.mrpController || []),
      validFrom: toDropdownOptions(filters.contractValidFrom || []),
      validTo: toDropdownOptions(filters.contractValidTo || []),
    }),
    [toDropdownOptions],
  );

  const getQuotaFilterList = useCallback(
    async (filtersState) => {
      try {
        const response = await dispatch(
          fetchQuotaMaintenanceFilterList({
           
            data: buildFilterListPayload(filtersState),
          }),
        );

        if (response?.meta?.requestStatus !== "fulfilled") {
          toast.error(
            response?.payload?.errorMessage ||
              "Failed to fetch quota filter options",
          );
          return;
        }

        setQuotaFilterOptions(
          mapFilterApiResponseToOptions(response?.payload?.filters || {}),
        );
        setIsFilterOptionsLoaded(true);
      } catch (error) {
        toast.error("Error fetching quota filter options");
        console.error("Error fetching quota filter options:", error);
      }
    },
    [
      buildFilterListPayload,
      dispatch,
      mapFilterApiResponseToOptions,
    ],
  );

  useEffect(() => {
    if (isResultsPage) {
      return;
    }
    getQuotaFilterList(appliedQuotaFilters);
  }, [getQuotaFilterList, isResultsPage]);

  const fallbackQuotaFilterOptions = useMemo(() => {
    const flattenedRows = apiData.flatMap((parent) => [
      parent,
      ...(parent?.children || []),
    ]);

    return {
      material: toDropdownOptions(flattenedRows.map((row) => row?.material)),
      plant: toDropdownOptions(flattenedRows.map((row) => row?.plant)),
      flow: toDropdownOptions(flattenedRows.map((row) => row?.flow)),
      sapNo: toDropdownOptions(flattenedRows.map((row) => row?.sapNo)),
      pmVendors: toDropdownOptions(flattenedRows.map((row) => row?.pmVendors)),
      externalMaterialGroup: toDropdownOptions(
        flattenedRows.map(
          (row) =>
            row?.externalMaterialGroup ||
            row?.extMaterialGroup ||
            row?.materialGroup,
        ),
      ),
      mrpController: toDropdownOptions(
        flattenedRows.map(
          (row) =>
            row?.mrpController ||
            row?.mrpControllerIdName ||
            row?.mrpControllerName,
        ),
      ),
      purchasingGroup: toDropdownOptions(
        flattenedRows.map(
          (row) =>
            row?.purchasingGroup ||
            row?.purchasingGrp ||
            row?.purchaseGroup,
        ),
      ),
      validFrom: toDropdownOptions(
        flattenedRows.map((row) => row?.validFrom || row?.quotaValidFrom),
      ),
      validTo: toDropdownOptions(
        flattenedRows.map((row) => row?.validTo || row?.quotaValidTo),
      ),
      sapStatus: toDropdownOptions(flattenedRows.map((row) => row?.sapStatus)),
      contractValidity: toDropdownOptions(
        flattenedRows.map((row) => row?.contractValidity),
      ),
      quotaValidity: toDropdownOptions(
        flattenedRows.map((row) => row?.quotaValidity),
      ),
    };
  }, [apiData, toDropdownOptions]);

  const resolvedQuotaFilterOptions = useMemo(
    () =>
      isFilterOptionsLoaded
        ? quotaFilterOptions
        : {
            ...emptyQuotaFilterOptions,
            ...fallbackQuotaFilterOptions,
          },
    [fallbackQuotaFilterOptions, isFilterOptionsLoaded, quotaFilterOptions],
  );

  const hasSelectedQuotaFilters = useMemo(
    () =>
      Object.values(quotaFilters).some(
        (items) => Array.isArray(items) && items.length > 0,
      ),
    [quotaFilters],
  );

  const isSearchQuotasDisabled = useMemo(
    () =>
      !Object.values(appliedQuotaFilters).some(
        (items) => Array.isArray(items) && items.length > 0,
      ),
    [appliedQuotaFilters],
  );

  const isViewAllQuotasDisabled = useMemo(
    () =>
      Object.values(appliedQuotaFilters).some(
        (items) => Array.isArray(items) && items.length > 0,
      ),
    [appliedQuotaFilters],
  );

  const handleMultiFilterChange = (name, items) => {
    const cleanedItems = (items || []).filter(
      (item) => item?.title !== "Select All",
    );
    setQuotaFilters((prev) => ({
      ...prev,
      [name]: cleanedItems,
    }));
  };

  const applyFilterSelection = async (name, items) => {
    const nextItems = items || quotaFilters[name] || [];
    const nextApplied = {
      ...appliedQuotaFilters,
      [name]: Array.isArray(nextItems) ? nextItems : [],
    };
    setAppliedQuotaFilters(cloneQuotaFiltersState(nextApplied));
    await getQuotaFilterList(nextApplied);
  };

  const handleSearchQuotas = async () => {
    const nextApplied = cloneQuotaFiltersState(quotaFilters);
    setAppliedQuotaFilters(nextApplied);
    const isSearchSuccessful = await getQuotaMaintenanceResults(nextApplied);
    if (isSearchSuccessful) {
      navigate("/quota-maintenance/results");
    }
  };

  const handleViewAllQuotas = async () => {
    const resetFilters = cloneQuotaFiltersState(initialQuotaFilters);
    setQuotaFilters(resetFilters);
    setAppliedQuotaFilters(resetFilters);
    try {
      const response = await dispatch(
        fetchQuotaMaintenanceViewAll({
          
        }),
      );

      if (response?.meta?.requestStatus !== "fulfilled") {
        toast.error(
          response?.payload?.errorMessage ||
            "Failed to fetch all quota arrangements",
        );
        return;
      }
    } catch (error) {
      toast.error("Error fetching all quota arrangements");
      console.error("Error fetching all quota arrangements:", error);
      return;
    }

    setSelectedQuotaCardKey("totalArrangements");
    writeSelectedQuotaCardToStorage("totalArrangements");
    navigate("/quota-maintenance/results");
  };

  const handleClearQuotaFilters = () => {
    const resetFilters = cloneQuotaFiltersState(initialQuotaFilters);
    setQuotaFilters(resetFilters);
    setAppliedQuotaFilters(resetFilters);
    getQuotaFilterList(resetFilters);
    clearQuotaFiltersFromStorage();
  };

  useEffect(() => {
    writeQuotaFiltersToStorage({
      quotaFilters: cloneQuotaFiltersState(quotaFilters),
      appliedQuotaFilters: cloneQuotaFiltersState(appliedQuotaFilters),
    });
  }, [quotaFilters, appliedQuotaFilters]);

  return (
    <>
      <TopSection
        labelList={[
          { title: "Home", path: "/landing-page" },
          { title: "Quota Maintenance", path: "/quota-maintenance" },
          ...(isResultsPage
            ? [{ title: "All Quotas", path: "/quota-maintenance/results" }]
            : []),
        ]}
        hideHeader={true}
        headerName={"Quota Maintenance"}
      />
      {quotaMaintenanceDetails?.loading ?
       <Loader />
      :
      <div
        style={{
          padding: "8px",
          background: "#f4f4f4",
          minHeight: "100vh",
        }}
      >
        {!isResultsPage && (
        <div
          style={{
            background: "#fff",
            borderRadius: "10px",
            padding: "18px",
            boxShadow: "0 1px 4px rgba(0,0,0,0.1)",
            marginBottom: "8px",
          }}
        >
          <div
            style={{
              fontSize: "16px",
              fontWeight: 700,
              color: "#111827",
              marginBottom: "16px",
            }}
          >
            Manual Quota Display
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, minmax(180px, 1fr))",
              gap: "12px",
              marginBottom: "16px",
            }}
          >
          
            <Filter label="Plant(No. + Description)">
              <MultiDropdown
                name="plant"
                options={resolvedQuotaFilterOptions.plant}
                selectedItemsData={quotaFilters.plant}
                onChangeItems={handleMultiFilterChange}
                onApplyItems={applyFilterSelection}
                showApply={true}
                dropdownWidth="100%"
              />
            </Filter>
            <Filter label="Material(No. + Description)">
              <MultiDropdown
                name="material"
                options={resolvedQuotaFilterOptions.material}
                selectedItemsData={quotaFilters.material}
                onChangeItems={handleMultiFilterChange}
                onApplyItems={applyFilterSelection}
                showApply={true}
                dropdownWidth="100%"
              />
            </Filter>
           
            <Filter label="Vendors(No. + Name)">
              <MultiDropdown
                name="pmVendors"
                options={resolvedQuotaFilterOptions.pmVendors}
                selectedItemsData={quotaFilters.pmVendors}
                onChangeItems={handleMultiFilterChange}
                onApplyItems={applyFilterSelection}
                showApply={true}
                dropdownWidth="100%"
              />
            </Filter>
            <Filter label="External Material Group">
              <MultiDropdown
                name="externalMaterialGroup"
                options={resolvedQuotaFilterOptions.externalMaterialGroup}
                selectedItemsData={quotaFilters.externalMaterialGroup}
                onChangeItems={handleMultiFilterChange}
                onApplyItems={applyFilterSelection}
                showApply={true}
                dropdownWidth="100%"
              />
            </Filter>
            <Filter label="MRP Controller(ID + Name)">
              <MultiDropdown
                name="mrpController"
                options={resolvedQuotaFilterOptions.mrpController}
                selectedItemsData={quotaFilters.mrpController}
                onChangeItems={handleMultiFilterChange}
                onApplyItems={applyFilterSelection}
                showApply={true}
                dropdownWidth="100%"
              />
            </Filter>
            <Filter label="Purchasing Group">
              <MultiDropdown
                name="purchasingGroup"
                options={resolvedQuotaFilterOptions.purchasingGroup}
                selectedItemsData={quotaFilters.purchasingGroup}
                onChangeItems={handleMultiFilterChange}
                onApplyItems={applyFilterSelection}
                showApply={true}
                dropdownWidth="100%"
              />
            </Filter>
            <Filter label="Valid From">
              <MultiDropdown
                name="validFrom"
                options={resolvedQuotaFilterOptions.validFrom}
                selectedItemsData={quotaFilters.validFrom}
                onChangeItems={handleMultiFilterChange}
                onApplyItems={applyFilterSelection}
                showApply={true}
                dropdownWidth="100%"
              />
            </Filter>
            <Filter label="Valid To">
              <MultiDropdown
                name="validTo"
                options={resolvedQuotaFilterOptions.validTo}
                selectedItemsData={quotaFilters.validTo}
                onChangeItems={handleMultiFilterChange}
                onApplyItems={applyFilterSelection}
                showApply={true}
                dropdownWidth="100%"
              />
            </Filter>
           
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "10px",
            }}
          >
            <ButtonComponent
              label="Search Quotas"
              bgColor="#111827"
              color="#fff"
              disabled={isSearchQuotasDisabled}
              onClick={handleSearchQuotas}
            />
            <ButtonComponent
              label="View All Quotas"
              bgColor="#111827"
              color="#fff"
              disabled={isViewAllQuotasDisabled}
              onClick={handleViewAllQuotas}
            />
            <ButtonComponent
              label="Clear All"
              bgColor="#fff"
              color="#111827"
              border="1px solid #d1d5db"
              disabled={!hasSelectedQuotaFilters}
              onClick={handleClearQuotaFilters}
            />
          </div>
        </div>
        )}

        {isResultsPage && (
          <>
       {/* <div
          style={{
            display: "flex",
            justifyContent: "flex-start",
            marginBottom: "12px",
          }}
        >
          <ButtonComponent
            label="Back"
            bgColor="#fff"
            color="#111827"
            border="1px solid #d1d5db"
            onClick={() => navigate("/quota-maintenance")}
          />
          
        </div>
        */}
        {/* METRIC CARDS */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(6, 1fr)",
            gap: "12px",
            marginBottom: "8px",
          }}
        >
          {cards.map((card) => {
            const isSelected = selectedQuotaCard?.key === card.key;

            return (
            <div
              key={card.key}
              role="button"
              tabIndex={0}
              onClick={() => handleQuotaCardClick(card.key)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  handleQuotaCardClick(card.key);
                }
              }}
              style={{
                background: "#fff",
                padding: "18px",
                borderRadius: "10px",
                boxShadow: "0 1px 4px rgba(0,0,0,0.1)",
                border: isSelected ? "2px solid #111827" : "1px solid #e5e7eb",
                boxSizing: "border-box",
                cursor: "pointer",
                outline: "none",
              }}
            >
              <div
                style={{
                  fontSize: "12px",
                  fontWeight: 600,
                  color: "#6b7280",
                  marginBottom: "10px",
                }}
              >
                {card.title}
              </div>

              <div
                style={{
                  fontSize: "42px",
                  fontWeight: "bold",
                  color: card.color,
                }}
              >
                {card.value}
              </div>
            </div>
            );
          })}
        </div>
        {/* QUOTA ARRANGEMENT GRID */}
        <div>
          <QuotaArrangementGrid
            statusFilter={selectedQuotaCard?.status || ""}
            onDuplicateSuccess={refreshQuotaMaintenanceResults}
          />
        </div>
          </>
        )}
      </div>}
    </>
  );
};

const Filter = ({ label, children }) => (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      gap: "6px",
      width: "100%",
      minWidth: 0,
    }}
  >
    <span style={{ fontSize: "13px", fontWeight: 700 }}>{label}</span>
    <div style={{ width: "100%" }}>{children}</div>
  </div>
);

export default QuotaMaintenance;